// modules/clientes/pages/ClientesPage.jsx
import React, { useState, useCallback, useEffect } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  TextField, MenuItem, InputAdornment, Grid,
  TableContainer, Table, TableHead, TableBody,
  TableRow, TableCell, TablePagination, Paper,
  IconButton, Chip, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Snackbar, Autocomplete, Tabs, Tab,
  Checkbox, FormControlLabel, Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon            from "@mui/icons-material/Search";
import AddIcon               from "@mui/icons-material/Add";
import VisibilityIcon        from "@mui/icons-material/Visibility";
import RefreshIcon           from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import EditIcon              from "@mui/icons-material/Edit";
import DeleteIcon            from "@mui/icons-material/Delete";
import ContactPhoneIcon      from "@mui/icons-material/ContactPhone";
import ReceiptLongIcon       from "@mui/icons-material/ReceiptLong";
import AssignmentIcon        from "@mui/icons-material/Assignment";
import InfoOutlinedIcon      from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon         from "@mui/icons-material/SwapHoriz";
import DevicesOtherIcon      from "@mui/icons-material/DevicesOther";
import DashboardLayout       from "../../../layouts/DashboardLayout";
import ClienteForm           from "../components/ClienteForm";
import ContactosServicioDialog from "../components/ContactosServicioDialog";
import InformacionFacturacionDialog from "../components/InformacionFacturacionDialog";
import ContratosServicioDialog from "../components/ContratosServicioDialog";
import ServicioInfoDialog from "../components/ServicioInfoDialog";
import ServicioCambiarClienteDialog from "../components/ServicioCambiarClienteDialog";
import ServicioEditDialog from "../components/ServicioEditDialog";
import HistorialCliente      from "../../historialCliente/pages/HistorialCliente";
import TruncatedCellText     from "../components/TruncatedCellText";
import { useClientes }       from "../context/ClienteContext";
import { ESTADOS_COLOR }     from "../../../utils/constants";
import * as clienteService   from "../services/clienteService";
import UsuarioAvatar from "../../../components/UsuarioAvatar";
import ENV from "../../../config/env";
import { SelectorImagenes, subirImagenesNuevas, VisorImagen } from "../components/ImagenesServicio";
import * as grupoService     from "../../grupos/services/grupoService";
import axios                 from "../../../config/axiosConfig";
import { useAuth }           from "../../../context/AuthContext";
import { PAGO_OPTIONS, getPagoLabel } from "../constants/pago";

// -- Estilos ---------------------------------------------------
const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800]
    : theme.palette.grey[100],
}));

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[900]
    : theme.palette.grey[50],
  color: theme.palette.text.secondary,
}));

const DEPARTAMENTOS = [
  "La Paz", "Cochabamba", "Santa Cruz", "Oruro",
  "Potosi", "Chuquisaca", "Tarija", "Beni", "Pando",
];

const RUBROS = [
  "Entidad Bancaria", "Financiera", "Transporte Valores",
  "Transporte Internacional-Nacional", "Flotas y Buses",
  "Seguridad", "Distribuidora", "Eventos y Publicidad",
  "Institucion Gubernamental", "Constructora",
  "Consulado o Embajada", "Doctores Hospital",
  "Import/Export", "Servicios en General",
];

// TKR no se puede transferir a otro cliente — mismo criterio que
// ContratosServicioDialog.jsx: cod_servicio=1 o el nombre incluye "TKR".
const esServicioTKR = (s) =>
  Number(s?.cod_servicio) === 1 || String(s?.nombre_servicio || "").toUpperCase().includes("TKR");

const MONEDA_LABEL = { 1: "USD", 2: "BOB" };
const MONEDAS      = [{ value: 1, label: "Dolares (USD)" }, { value: 2, label: "Bolivianos (BOB)" }];
const SUCURSALES   = ["La Paz", "Cochabamba", "Santa Cruz"];
const TIPOS_SERV   = ["Nacional", "Sucursal"];
const TITULOS_CONTACTO = [
  "Senor", "Senora",
  "Licenciado", "Licenciada",
  "Ingeniero", "Ingeniera",
  "Doctor", "Doctora",
];

const CONTACTO_CLIENTE_INI = {
  titulo: "Senor",
  nombre: "",
  paterno: "",
  materno: "",
  ci: "",
  cargo_contacto: "",
  telefono_contacto: "",
  celular: "",
  correo: "",
  departamento: "",
};

const PU_LABELS_BY_SERVICIO = {
  1: {
    pu_1: "P.U. Portatil",
    pu_2: "P.U. Movil",
    pu_4: "P.U. Base",
    pu_3: "P.U. Servicio",
  },
  2: {
    pu_1: "P.U. Alquiler",
    pu_2: "P.U. Servicio Tecnico",
    pu_4: "P.U. Servicio Nacional",
    pu_3: "P.U. Servicio Internacional",
  },
};

const getPuLabels = (codServicio) => PU_LABELS_BY_SERVICIO[Number(codServicio)] || null;

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const getEjecutivoLabel = (option) =>
  option?.nombre ? `${option.nombre} (${option.username})` : option?.username || "";

const getEjecutivoValue = (ejecutivos, username) =>
  ejecutivos.find((u) => u.username === username) ||
  (username ? { username, nombre: username } : null);

const PreciosServicioFields = ({ codServicio, values, onChange, disabled }) => {
  const labels = getPuLabels(codServicio);
  if (!labels) return null;

  return (
    <>
      {Object.entries(labels).map(([name, label]) => (
        <Grid key={name} size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={label}
            name={name}
            value={values[name] ?? ""}
            onChange={onChange}
            disabled={disabled}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>
      ))}
    </>
  );
};

const ClienteCampo = ({ label, value }) => (
  <Box sx={{
    minWidth: 0,
    height: "100%",
    p: 1,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    bgcolor: "background.paper",
  }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2, mb: 0.35 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const getContactoClienteId = (contacto) => contacto?.id_contacto ?? contacto?.id;

const getNombreContactoCliente = (contacto) => {
  const partes = [contacto?.nombre, contacto?.paterno, contacto?.materno].filter(Boolean);
  return partes.length ? partes.join(" ") : contacto?.nombre_contacto || "";
};

const ClienteContactosTab = ({ cliente }) => {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CONTACTO_CLIENTE_INI);

  const cargarContactos = useCallback(async () => {
    if (!cliente?.cod_cliente) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await clienteService.getContactos(cliente.cod_cliente);
      const rows = Array.isArray(data) ? data : data?.contactos;
      setContactos(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar los contactos");
      setContactos([]);
    } finally {
      setLoading(false);
    }
  }, [cliente?.cod_cliente]);

  useEffect(() => {
    setEditing(null);
    setForm(CONTACTO_CLIENTE_INI);
    setFormError(null);
    cargarContactos();
  }, [cargarContactos]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNew = () => {
    setEditing(null);
    setForm(CONTACTO_CLIENTE_INI);
    setFormError(null);
  };

  const handleEdit = (contacto) => {
    setEditing(contacto);
    setForm({
      titulo: contacto.titulo || "Senor",
      nombre: contacto.nombre || contacto.nombre_contacto || "",
      paterno: contacto.paterno || "",
      materno: contacto.materno || "",
      ci: contacto.ci || "",
      cargo_contacto: contacto.cargo_contacto || "",
      telefono_contacto: contacto.telefono_contacto || contacto.telefono || "",
      celular: contacto.celular || "",
      correo: contacto.correo || "",
      departamento: contacto.departamento || "",
    });
    setFormError(null);
  };

  const handleSaveContacto = async () => {
    if (!form.titulo) {
      setFormError("El titulo es requerido");
      return;
    }
    if (!form.nombre.trim()) {
      setFormError("El nombre es requerido");
      return;
    }
    if (!form.paterno.trim()) {
      setFormError("El apellido paterno es requerido");
      return;
    }
    if (!form.departamento) {
      setFormError("El departamento es requerido");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        nombre: form.nombre.trim(),
        paterno: form.paterno.trim(),
        materno: form.materno.trim(),
        ci: form.ci.trim(),
        cargo_contacto: form.cargo_contacto.trim(),
        telefono_contacto: form.telefono_contacto.trim(),
        celular: form.celular.trim(),
        correo: form.correo.trim(),
      };

      const contactoId = getContactoClienteId(editing);
      if (contactoId) {
        await clienteService.updateContacto(cliente.cod_cliente, contactoId, payload);
      } else {
        await clienteService.createContacto(cliente.cod_cliente, payload);
      }

      await cargarContactos();
      handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo guardar el contacto");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContacto = async (contacto) => {
    const contactoId = getContactoClienteId(contacto);
    if (!contactoId) return;

    const nombre = getNombreContactoCliente(contacto) || "este contacto";
    if (!window.confirm(`Eliminar ${nombre}?`)) return;

    setDeletingId(contactoId);
    setFormError(null);
    try {
      await clienteService.deleteContacto(cliente.cod_cliente, contactoId);
      await cargarContactos();
      if (getContactoClienteId(editing) === contactoId) handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo eliminar el contacto");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ ...DIALOG_COMPACT_SX, mb: 1.5 }}>
        <Grid container spacing={1}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField select fullWidth required size="small" label="Titulo" name="titulo"
              value={form.titulo} onChange={handleFormChange} disabled={saving}>
              {TITULOS_CONTACTO.map((titulo) => (
                <MenuItem key={titulo} value={titulo}>{titulo}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth required size="small" label="Nombre" name="nombre"
              value={form.nombre} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth required size="small" label="Paterno" name="paterno"
              value={form.paterno} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Materno" name="materno"
              value={form.materno} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField fullWidth size="small" label="CI" name="ci"
              value={form.ci} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="Cargo" name="cargo_contacto"
              value={form.cargo_contacto} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField select fullWidth required size="small" label="Departamento" name="departamento"
              value={form.departamento} onChange={handleFormChange} disabled={saving}>
              {DEPARTAMENTOS.map((departamento) => (
                <MenuItem key={departamento} value={departamento}>{departamento}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField fullWidth size="small" label="Telefono" name="telefono_contacto"
              value={form.telefono_contacto} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField fullWidth size="small" label="Celular" name="celular"
              value={form.celular} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" type="email" label="Correo" name="correo"
              value={form.correo} onChange={handleFormChange} disabled={saving} />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={handleNew} disabled={saving}
              startIcon={<AddIcon />} sx={{ minHeight: 32 }}>
              Nuevo
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveContacto} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ minHeight: 32 }}>
              {editing ? "Actualizar" : "Guardar"}
            </Button>
          </Grid>
        </Grid>
        {formError && <Alert severity="error" sx={{ mt: 1.5 }}>{formError}</Alert>}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Cargando contactos...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !contactos.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Sin contactos registrados para este cliente
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SubHeaderCell>Nombre</SubHeaderCell>
                <SubHeaderCell>Cargo</SubHeaderCell>
                <SubHeaderCell>Departamento</SubHeaderCell>
                <SubHeaderCell>Telefono</SubHeaderCell>
                <SubHeaderCell>Celular</SubHeaderCell>
                <SubHeaderCell>Correo</SubHeaderCell>
                <SubHeaderCell align="center">Accion</SubHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contactos.map((contacto) => {
                const contactoId = getContactoClienteId(contacto);
                return (
                  <TableRow key={contactoId ?? `${contacto.nombre}-${contacto.paterno}`} hover>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={getNombreContactoCliente(contacto)} maxWidth={190} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.cargo_contacto || "-"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.departamento || "-"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      {contacto.telefono_contacto || contacto.telefono || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.celular || "-"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={contacto.correo} maxWidth={190} />
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar contacto">
                        <IconButton size="small" onClick={() => handleEdit(contacto)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar contacto">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteContacto(contacto)}
                            disabled={!contactoId || deletingId === contactoId}
                          >
                            {deletingId === contactoId
                              ? <CircularProgress size={14} />
                              : <DeleteIcon sx={{ fontSize: 15 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const ClienteDetalleDialog = ({ open, cliente, onClose, onSaved }) => {
  const [activeTab, setActiveTab] = useState("datos");
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ ...cliente });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setActiveTab("datos");
    setEditando(false);
    setForm({ ...cliente });
    setError(null);
  }, [open, cliente]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setForm({ ...cliente });
    setError(null);
    setEditando(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = form;

      await clienteService.update(cliente.cod_cliente, payload);
      setEditando(false);
      onSaved?.({ ...cliente, ...payload });
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar el cliente");
    } finally {
      setLoading(false);
    }
  };

  const compactFieldSx = {
    "& .MuiInputBase-input": { py: 0.75, fontSize: 13 },
    "& .MuiInputLabel-root": { fontSize: 13 },
    "& .MuiSelect-select": { py: 0.75 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2.5,
        py: 1.75,
        bgcolor: "action.hover",
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {cliente.razon_social}
            </Typography>
            <Chip
              label={cliente.estado || "-"}
              size="small"
              color={ESTADOS_COLOR[cliente.estado] || "default"}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Cliente {cliente.cod_cliente} {cliente.cod_usuario ? `- Registrado por ${cliente.cod_usuario}` : ""}
          </Typography>
        </Box>
        {activeTab === "datos" && !editando ? (
          <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => setEditando(true)}>
            Editar
          </Button>
        ) : null}
      </DialogTitle>
      <Divider />
      <Box sx={{ px: 2.5, bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="datos" label="Datos" />
          <Tab value="contactos" label="Contactos" disabled={editando} />
        </Tabs>
      </Box>
      <DialogContent sx={{ bgcolor: "background.default", px: 2.5, py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {activeTab === "contactos" ? (
          <ClienteContactosTab cliente={cliente} />
        ) : !editando ? (
          <Grid container spacing={1.25} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ClienteCampo label="Razon social" value={cliente.razon_social} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <ClienteCampo label="Codigo" value={cliente.cod_cliente} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <ClienteCampo label="Estado" value={cliente.estado} />
            </Grid>
            <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Tipo persona" value={cliente.tipo_persona} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="C.I." value={cliente.ci} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Primer nombre" value={cliente.primer_nombre} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Segundo nombre" value={cliente.segundo_nombre} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Apellido paterno" value={cliente.apellido_paterno} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Apellido materno" value={cliente.apellido_materno} />
            </Grid>
            <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ClienteCampo label="Rubro" value={cliente.rubro} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ClienteCampo label="Departamento" value={cliente.departamento} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ClienteCampo label="Tipo de cliente" value={cliente.tipo_cliente} />
            </Grid>
            <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <ClienteCampo label="Direccion" value={cliente.direccion} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <ClienteCampo label="Telefono" value={cliente.telefono} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <ClienteCampo label="Celular" value={cliente.celular} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Fax" value={cliente.fax} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <ClienteCampo label="Correo oficial" value={cliente.correo_ofi} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <ClienteCampo label="Pagina web" value={cliente.pagina_web} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ClienteCampo label="Registrado por" value={cliente.cod_usuario} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ClienteCampo label="Observaciones" value={cliente.observaciones} />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1.25} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Razon social" name="razon_social"
                  value={form.razon_social || ""} onChange={handleChange} disabled={loading}
                  sx={compactFieldSx} />
              </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Codigo"
                value={cliente.cod_cliente || ""} disabled sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField select fullWidth size="small" label="Estado" name="estado"
                value={form.estado || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx}>
                {Object.keys(ESTADOS_COLOR).map((estado) => (
                  <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select fullWidth size="small" label="Tipo persona" name="tipo_persona"
                value={form.tipo_persona || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx}>
                <MenuItem value="">Sin tipo</MenuItem>
                {TITULOS_CONTACTO.map((titulo) => (
                  <MenuItem key={titulo} value={titulo}>{titulo}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="C.I." name="ci"
                value={form.ci || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Primer nombre" name="primer_nombre"
                value={form.primer_nombre || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Segundo nombre" name="segundo_nombre"
                value={form.segundo_nombre || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Apellido paterno" name="apellido_paterno"
                value={form.apellido_paterno || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Apellido materno" name="apellido_materno"
                value={form.apellido_materno || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField select fullWidth size="small" label="Rubro o actividad" name="rubro"
                value={form.rubro || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx}>
                <MenuItem value="">Sin rubro</MenuItem>
                {RUBROS.map((rubro) => <MenuItem key={rubro} value={rubro}>{rubro}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField select fullWidth size="small" label="Departamento" name="departamento"
                value={form.departamento || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx}>
                {DEPARTAMENTOS.map((departamento) => (
                  <MenuItem key={departamento} value={departamento}>{departamento}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Tipo de cliente" name="tipo_cliente"
                value={form.tipo_cliente || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Direccion" name="direccion"
                value={form.direccion || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Telefono" name="telefono"
                value={form.telefono || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Celular" name="celular"
                value={form.celular || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField fullWidth size="small" label="Fax" name="fax"
                value={form.fax || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="email" label="Correo oficial" name="correo_ofi"
                value={form.correo_ofi || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Pagina web" name="pagina_web"
                value={form.pagina_web || ""} onChange={handleChange} disabled={loading} sx={compactFieldSx} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" multiline minRows={1} maxRows={3} label="Observaciones"
                name="observaciones" value={form.observaciones || ""} onChange={handleChange} disabled={loading}
                sx={compactFieldSx} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {activeTab === "contactos" ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : !editando ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button onClick={handleCancel} disabled={loading}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave} disabled={loading}
              startIcon={loading ? <CircularProgress size={14} /> : null}>
              {loading ? "Guardando..." : "Guardar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const GrupoAutocomplete = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (!open) return;

    let activo = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await grupoService.buscar(inputValue.trim());
        if (activo) setOptions(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setOptions([]);
      } finally {
        if (activo) setLoading(false);
      }
    }, 250);

    return () => {
      activo = false;
      clearTimeout(timer);
    };
  }, [open, inputValue]);

  const setGrupo = (nombre) => {
    const limpio = nombre || "";
    setInputValue(limpio);
    onChange({ target: { name: "grupo", value: limpio } });
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      value={value || ""}
      filterOptions={(x) => x}
      onInputChange={(_, newInputValue) => setGrupo(newInputValue)}
      onChange={(_, newValue) => {
        setGrupo(typeof newValue === "string" ? newValue : newValue?.nombre || "");
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.nombre || ""
      }
      isOptionEqualToValue={(option, selected) =>
        (option?.nombre || option) === (selected?.nombre || selected)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Grupo"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const useEjecutivosTRS = (open) => {
  const { user } = useAuth();
  const usernameActual = user?.username?.trim() || "";
  const sucursalUsuario = user?.sucursal?.trim() || "";
  const sucursalFacturarDefault = SUCURSALES.includes(sucursalUsuario) ? sucursalUsuario : "";
  const [ejecutivos, setEjecutivos] = useState([]);
  const [loadingEjecutivos, setLoadingEjecutivos] = useState(false);

  useEffect(() => {
    if (!open || !sucursalUsuario) {
      setEjecutivos([]);
      return;
    }

    let activo = true;
    const cargarEjecutivos = async () => {
      setLoadingEjecutivos(true);
      try {
        const { data } = await axios.get("/api/usuarios-trs", {
          params: { sucursal: sucursalUsuario, estado: "A" },
        });
        if (activo) setEjecutivos(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setEjecutivos([]);
      } finally {
        if (activo) setLoadingEjecutivos(false);
      }
    };

    cargarEjecutivos();
    return () => { activo = false; };
  }, [open, sucursalUsuario]);

  return { ejecutivos, loadingEjecutivos, usernameActual, sucursalFacturarDefault };
};

// -- Dialogo editar servicio -----------------------------------
// -- Fila expandible -------------------------------------------
const SERVICIO_NUEVO_INI = {
  cod_servicio      : "",
  grupo             : "",
  nombre            : "",
  descripcion       : "",
  tiempo_conservacion_recorrido: "",
  estado            : "Activo",
  sucursal_facturar : "",
  pago              : "vencido",
  moneda            : 2,
  tipo              : "Nacional",
  ejecutivo_venta   : "",
  correo            : "",
  observacion       : "",
  central_riesgo    : "No",
  pu_1              : "",
  pu_2              : "",
  pu_3              : "",
  pu_4              : "",
};

const ServicioCreateDialog = ({ open, cliente, catalogos, onClose, onCreated }) => {
  const [imagenes, setImagenes] = useState([]);
  const [creado,   setCreado]   = useState(null); // si falla la subida de imágenes, al reintentar no se vuelve a crear el servicio
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const {
    ejecutivos,
    loadingEjecutivos,
    usernameActual,
    sucursalFacturarDefault,
  } = useEjecutivosTRS(open);
  const [form, setForm] = useState({
    ...SERVICIO_NUEVO_INI,
    ejecutivo_venta   : usernameActual,
    sucursal_facturar : sucursalFacturarDefault,
  });

  useEffect(() => {
    if (!open) return;
    setForm((p) => ({
      ...p,
      sucursal_facturar: p.sucursal_facturar || sucursalFacturarDefault,
      ejecutivo_venta  : p.ejecutivo_venta || usernameActual,
    }));
  }, [open, sucursalFacturarDefault, usernameActual]);

  const handle = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.cod_servicio) return setError("Debe seleccionar un tipo de servicio");
    if (!form.descripcion) return setError("La descripcion es requerida");
    if (!form.estado) return setError("El estado es requerido");

    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...form,
        grupo      : (form.grupo || "").trim(),
        nombre     : (form.nombre || "").trim(),
        descripcion: (form.descripcion || "").trim(),
      };

      let servicio = creado;
      if (!servicio) {
        const { data } = await clienteService.createServicio(cliente.cod_cliente, payload);
        servicio = data;
        setCreado(data);
      }
      if (imagenes.length) {
        try {
          await subirImagenesNuevas(cliente.cod_cliente, servicio.cod_servicio_cliente, imagenes);
        } catch (e) {
          setError("El servicio se creó, pero no se pudieron subir las imágenes. Pulsa Guardar para reintentar solo las imágenes.");
          return;
        }
      }
      onCreated(servicio);
    } catch (err) {
      setError(err?.response?.data?.message || "Error al crear el servicio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Dialogo nuevo servicio */}
      <Divider />
      <DialogContent>
        <Box sx={DIALOG_COMPACT_SX}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth required size="small" label="Tipo de Servicio"
                name="cod_servicio" value={form.cod_servicio} onChange={handle} disabled={loading}>
                <MenuItem value="">Seleccionar...</MenuItem>
                {(catalogos.servicios || []).map((s) => (
                  <MenuItem key={s.id_servicio} value={s.id_servicio}>{s.nombre}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <GrupoAutocomplete
                value={form.grupo}
                onChange={handle}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Nombre"
                name="nombre" value={form.nombre} onChange={handle} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Descripcion"
                name="descripcion" value={form.descripcion} onChange={handle} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Tiempo de Conservacion de Recorrido"
                name="tiempo_conservacion_recorrido" value={form.tiempo_conservacion_recorrido} onChange={handle} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth required size="small" label="Estado"
                name="estado" value={form.estado} onChange={handle} disabled={loading}>
                {Object.keys(ESTADOS_COLOR).map((e) => (
                  <MenuItem key={e} value={e}>{e}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth size="small" label="Sucursal a Facturar"
                name="sucursal_facturar" value={form.sucursal_facturar} onChange={handle} disabled={loading}>
                <MenuItem value="">Seleccionar...</MenuItem>
                {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth size="small" label="Forma de Pago"
                name="pago" value={form.pago} onChange={handle} disabled={loading}>
                {PAGO_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth size="small" label="Moneda"
                name="moneda" value={form.moneda} onChange={handle} disabled={loading}>
                {MONEDAS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth size="small" label="Tipo"
                name="tipo" value={form.tipo} onChange={handle} disabled={loading}>
                {TIPOS_SERV.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <PreciosServicioFields
              codServicio={form.cod_servicio}
              values={form}
              onChange={handle}
              disabled={loading}
            />
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                disabled={loading}
                options={ejecutivos}
                loading={loadingEjecutivos}
                value={getEjecutivoValue(ejecutivos, form.ejecutivo_venta)}
                onChange={(_, value) => {
                  setForm((p) => ({ ...p, ejecutivo_venta: value?.username || "" }));
                }}
                getOptionLabel={getEjecutivoLabel}
                isOptionEqualToValue={(option, value) => option.username === value.username}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ejecutivo de Ventas"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingEjecutivos && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Correo" type="email"
                name="correo" value={form.correo} onChange={handle} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" multiline rows={2} label="Observacion"
                name="observacion" value={form.observacion} onChange={handle} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SelectorImagenes value={imagenes} onChange={setImagenes} disabled={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={form.central_riesgo === "Cliente Reportado"}
                    onChange={(e) => setForm((p) => ({
                      ...p, central_riesgo: e.target.checked ? "Cliente Reportado" : "No",
                    }))}
                    disabled={loading}
                  />
                )}
                label="Central de Riesgo"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : null}>
          {loading ? "Guardando..." : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ClienteRow = ({ c, catalogos, onClienteUpdated }) => {
  const [open,         setOpen]         = useState(false);
  const [servicios,    setServicios]    = useState(null);
  const [cargando,     setCargando]     = useState(false);
  const [errSvc,       setErrSvc]       = useState(null);
  const [detalleDe,    setDetalleDe]    = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [contactosDe,  setContactosDe]  = useState(null);
  const [facturacionDe,setFacturacionDe]= useState(null);
  const [contratosDe,  setContratosDe]  = useState(null);
  const [infoDe,       setInfoDe]       = useState(null);
  const [transferiendo,setTransferiendo]= useState(null);
  const [equiposDe,    setEquiposDe]    = useState(null);
  const [creando,      setCreando]      = useState(false);
  const [snack,        setSnack]        = useState(false);
  const [imagenVista,  setImagenVista]  = useState(null);

  const cargarServicios = useCallback(async () => {
    setCargando(true);
    setErrSvc(null);
    try {
      const { data } = await clienteService.getServiciosResumen(c.cod_cliente);
      setServicios(Array.isArray(data) ? data : []);
    } catch {
      setErrSvc("No se pudieron cargar los servicios");
      setServicios([]);
    } finally {
      setCargando(false);
    }
  }, [c.cod_cliente]);

  const toggleExpand = useCallback(async () => {
    if (!open && servicios === null) await cargarServicios();
    setOpen((prev) => !prev);
  }, [open, servicios, cargarServicios]);

  const handleSaved = (updated) => {
    setServicios((prev) =>
      prev.map((s) =>
        s.cod_servicio_cliente === updated.cod_servicio_cliente ? { ...s, ...updated } : s
      )
    );
    setEditando(null);
    setSnack(true);
  };

  const handleMoved = (updated) => {
    setServicios((prev) =>
      (prev || []).filter((s) => s.cod_servicio_cliente !== updated.cod_servicio_cliente)
    );
    setTransferiendo(null);
    setSnack(true);
  };

  const handleCreated = async () => {
    setCreando(false);
    setOpen(true);
    await cargarServicios();
    setSnack(true);
  };

  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? 0 : undefined } }}>
        <TableCell>{c.cod_cliente}</TableCell>
        <TableCell>
          <TruncatedCellText value={c.razon_social} maxWidth={360} />
        </TableCell>
        <TableCell>
          <TruncatedCellText value={c.rubro} maxWidth={230} />
        </TableCell>
        <TableCell>{c.departamento}</TableCell>
        <TableCell>{c.telefono || c.celular || "-"}</TableCell>
        <TableCell><UsuarioAvatar usuario={c.cod_usuario} foto={c.usuario_foto} /></TableCell>
        <TableCell>
          <Chip
            label={c.estado}
            size="small"
            color={ESTADOS_COLOR[c.estado] || "default"}
          />
        </TableCell>
        <TableCell
          align="center"
          sx={{
            whiteSpace: "nowrap",
            position: "sticky",
            right: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            boxShadow: "-2px 0 4px rgba(0,0,0,0.12)",
          }}
        >
          <Tooltip title="Ver detalle">
            <IconButton size="small" onClick={() => setDetalleDe(c)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Agregar servicio">
            <IconButton size="small" onClick={() => setCreando(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={open ? "Ocultar servicios" : "Ver servicios"}>
            <IconButton size="small" onClick={toggleExpand}>
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* -- Fila de servicios expandida -- */}
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 4, py: 1.5, bgcolor: "action.hover" }}>
              {cargando ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Cargando servicios...
                  </Typography>
                </Box>
              ) : errSvc ? (
                <Alert severity="error" sx={{ py: 0.5 }}>{errSvc}</Alert>
              ) : !servicios?.length ? (
                <Typography variant="caption" color="text.secondary">
                  Sin servicios registrados
                </Typography>
              ) : (
                <Table size="small" sx={{ bgcolor: "background.paper", borderRadius: 1, overflow: "hidden" }}>
                  <TableHead>
                    <TableRow>
                      <SubHeaderCell>#</SubHeaderCell>
                      <SubHeaderCell>Servicio</SubHeaderCell>
                      <SubHeaderCell>Grupo</SubHeaderCell>
                      <SubHeaderCell>Nombre</SubHeaderCell>
                      <SubHeaderCell>Descripcion</SubHeaderCell>
                      <SubHeaderCell>Estado</SubHeaderCell>
                      <SubHeaderCell>Sucursal</SubHeaderCell>
                      <SubHeaderCell>Pago</SubHeaderCell>
                      <SubHeaderCell>Moneda</SubHeaderCell>
                      <SubHeaderCell>Tipo</SubHeaderCell>
                      <SubHeaderCell>Ejecutivo</SubHeaderCell>
                      <SubHeaderCell
                        align="center"
                        sx={{ position: "sticky", right: 0, zIndex: 2, boxShadow: "-2px 0 4px rgba(0,0,0,0.12)" }}
                      >
                        Acciones
                      </SubHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {servicios.map((s) => (
                      <TableRow key={s.cod_servicio_cliente} hover>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{s.cod_servicio_cliente}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {s.imagen_principal ? (
                              <Tooltip title="Ver imagen del servicio" arrow disableInteractive>
                                <Avatar
                                  variant="rounded" src={`${ENV.API_BASE_URL}${s.imagen_principal}`} alt={s.nombre_servicio}
                                  imgProps={{ loading: "lazy" }} onClick={() => setImagenVista({ src: `${ENV.API_BASE_URL}${s.imagen_principal}`, titulo: s.nombre_servicio })}
                                  sx={{ width: 30, height: 30, cursor: "zoom-in", flexShrink: 0 }}
                                />
                              </Tooltip>
                            ) : null}
                            <span>{s.nombre_servicio || "-"}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{s.grupo || s.grupos || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{s.nombre}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>
                          {s.descripcion || "-"}
                          {s.tiempo_conservacion_recorrido && (
                            <Typography
                              variant="caption"
                              title={`Guardar recorrido: ${s.tiempo_conservacion_recorrido}`}
                              sx={{
                                display: "block",
                                mt: 0.15,
                                fontSize: "0.68rem",
                                lineHeight: 1.2,
                                fontWeight: 700,
                                color: "#d81b60",
                              }}
                            >
                              {s.tiempo_conservacion_recorrido}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.estado}
                            size="small"
                            color={ESTADOS_COLOR[s.estado] || "default"}
                            sx={{ fontSize: "0.68rem", height: 18 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{s.sucursal_facturar || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{getPagoLabel(s.pago) || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{MONEDA_LABEL[s.moneda] || s.moneda}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{s.tipo || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }}><UsuarioAvatar usuario={s.ejecutivo_venta} foto={s.ejecutivo_foto} size={20} /></TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace: "nowrap",
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            bgcolor: "background.paper",
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.12)",
                          }}
                        >
                          <Tooltip title="Mas informacion">
                            <IconButton size="small" onClick={() => setInfoDe(s)}>
                              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver equipos del servicio">
                            <IconButton size="small" onClick={() => setEquiposDe(s)}>
                              <DevicesOtherIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver contactos">
                            <IconButton size="small" onClick={() => setContactosDe(s)}>
                              <ContactPhoneIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={Number(s.legal_activos_count) > 0 ? "Informacion de facturacion" : "Informacion de facturacion (sin datos activos)"}>
                            <IconButton size="small" onClick={() => setFacturacionDe(s)} color={Number(s.legal_activos_count) > 0 ? "success" : "error"}>
                              <ReceiptLongIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Contratos">
                            <IconButton size="small" onClick={() => setContratosDe(s)}>
                              <AssignmentIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar servicio">
                            <IconButton size="small" onClick={() => setEditando(s)}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={esServicioTKR(s) ? "No disponible para servicios TKR" : "Cambiar cliente del servicio"}>
                            <span>
                              <IconButton size="small" onClick={() => setTransferiendo(s)} disabled={esServicioTKR(s)}>
                                <SwapHorizIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* -- Dialogo editar servicio -- */}
      {detalleDe && (
        <ClienteDetalleDialog
          open
          cliente={detalleDe}
          onClose={() => setDetalleDe(null)}
          onSaved={(updated) => {
            setDetalleDe(updated);
            onClienteUpdated?.();
          }}
        />
      )}

      {editando && (
        <ServicioEditDialog
          open
          servicio={editando}
          onClose={() => setEditando(null)}
          onSaved={handleSaved}
        />
      )}

      {transferiendo && (
        <ServicioCambiarClienteDialog
          open
          clienteActual={c}
          servicio={transferiendo}
          onClose={() => setTransferiendo(null)}
          onMoved={handleMoved}
        />
      )}

      {/* Dialogo nuevo servicio */}
      {contactosDe && (
        <ContactosServicioDialog
          open
          cliente={c}
          servicio={contactosDe}
          onClose={() => setContactosDe(null)}
        />
      )}

      {facturacionDe && (
        <InformacionFacturacionDialog
          open
          cliente={c}
          servicio={facturacionDe}
          onClose={() => setFacturacionDe(null)}
        />
      )}

      {infoDe && (
        <ServicioInfoDialog
          open
          cliente={c}
          servicio={infoDe}
          onClose={() => setInfoDe(null)}
        />
      )}

      {equiposDe && (
        <Dialog
          open
          onClose={() => setEquiposDe(null)}
          maxWidth="xl"
          fullWidth
          PaperProps={{ sx: { borderRadius: 1.25, maxHeight: "92vh" } }}
        >
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Equipos del servicio #{equiposDe.cod_servicio_cliente}
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              {c.razon_social} - {equiposDe.servicio_nombre || equiposDe.nombre_servicio || "Servicio"}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1, bgcolor: "background.default" }}>
            <HistorialCliente
              codServicioClienteProp={equiposDe.cod_servicio_cliente}
              embedded
              onBack={() => setEquiposDe(null)}
            />
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={() => setEquiposDe(null)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {contratosDe && (
        <ContratosServicioDialog
          open
          cliente={c}
          servicio={contratosDe}
          onClose={() => setContratosDe(null)}
        />
      )}

      {creando && (
        <ServicioCreateDialog
          open
          cliente={c}
          catalogos={catalogos}
          onClose={() => setCreando(false)}
          onCreated={handleCreated}
        />
      )}

      <VisorImagen imagen={imagenVista} onClose={() => setImagenVista(null)} />

      {/* -- Confirmacion guardado -- */}
      <Snackbar
        open={snack}
        autoHideDuration={3000}
        onClose={() => setSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnack(false)} sx={{ width: "100%" }}>
          Servicio guardado correctamente
        </Alert>
      </Snackbar>
    </>
  );
};

// -- Componente principal --------------------------------------
const ClientesPage = () => {
  const {
    clientes, total, loading, error,
    filtros, catalogos,
    setFiltro, setPage, resetFiltros, refetch,
  } = useClientes();

  const [openForm,   setOpenForm]   = useState(false);
  const [buscarTemp, setBuscarTemp] = useState("");

  const handleBuscar = () => setFiltro({ buscar: buscarTemp });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleBuscar();
  };

  const handleSuccess = () => {
    setOpenForm(false);
    refetch();
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 2 }}>

        {/* -- Header -- */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Clientes</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenForm(true)}>
            Nuevo
          </Button>
        </Box>

        {/* -- Filtros Compactos -- */}
        <Paper sx={{ p: 1, mb: 1.5 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth size="small"
                label="Buscar por codigo o razon social"
                value={buscarTemp}
                onChange={(e) => setBuscarTemp(e.target.value)}
                onKeyDown={handleKeyDown}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: 36 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleBuscar}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                select fullWidth size="small" label="Estado"
                value={filtros.estado}
                onChange={(e) => setFiltro({ estado: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: 36 } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.keys(ESTADOS_COLOR).map((e) => (
                  <MenuItem key={e} value={e}>{e}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                select fullWidth size="small" label="Departamento"
                value={filtros.departamento}
                onChange={(e) => setFiltro({ departamento: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: 36 } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {DEPARTAMENTOS.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                select fullWidth size="small" label="Servicio"
                value={filtros.cod_servicio}
                onChange={(e) => setFiltro({ cod_servicio: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: 36 } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {catalogos.servicios.map((s) => (
                  <MenuItem key={s.id_servicio} value={s.id_servicio}>{s.nombre}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                select fullWidth size="small" label="Registrado por"
                value={filtros.cod_usuario}
                onChange={(e) => setFiltro({ cod_usuario: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { height: 36 } }}
              >
                <MenuItem value="">Todos</MenuItem>
                {catalogos.usuarios.map((u) => (
                  <MenuItem key={u.cod_usuario} value={u.cod_usuario}>{u.cod_usuario}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Limpiar filtros">
                <Button fullWidth variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={resetFiltros} sx={{ height: 36, minWidth: 0, px: 1 }}>
                  Limpiar
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {/* -- Error -- */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} action={
            <Button size="small" onClick={refetch}>Reintentar</Button>
          }>
            {error}
          </Alert>
        )}

        {/* -- Tabla -- */}
        <Paper>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell sx={{ width: 92 }}>Codigo</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 380 }}>Razon Social</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 250 }}>Rubro</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 125 }}>Departamento</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 105 }}>Telefono</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 120 }}>Registrado por</StyledHeaderCell>
                  <StyledHeaderCell sx={{ width: 100 }}>Estado</StyledHeaderCell>
                  <StyledHeaderCell
                    align="center"
                    sx={{ width: 124, position: "sticky", right: 0, zIndex: 3, boxShadow: "-2px 0 4px rgba(0,0,0,0.12)" }}
                  >
                    Acciones
                  </StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : clientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No se encontraron clientes
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clientes.map((c) => (
                    <ClienteRow
                      key={c.cod_cliente}
                      c={c}
                      catalogos={catalogos}
                      onClienteUpdated={refetch}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={filtros.page - 1}
            rowsPerPage={filtros.pageSize}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPageOptions={[50]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>

        {/* -- Form nuevo cliente -- */}
        {openForm && (
          <ClienteForm
            open={openForm}
            onClose={() => setOpenForm(false)}
            onSuccess={handleSuccess}
            catalogos={catalogos}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ClientesPage;


